import React, { useState, useEffect} from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/CreateNotification.css';
import api from '../utils/axiosConfig';

const CreateNotificationAdmin = () => {
  const [formData, setFormData] = useState({
    userCode: '',
    sessionName: '',
    startDate: '',
    singleTime: '',  // New field for single notification time
    endDate: '',
    notificationTime: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    try {
      if (isRecurring) {
        // Create notifications for each weekday in the range
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const timeParts = formData.notificationTime.split(':');
        
        const notifications = [];
        for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
          // Skip weekends
          if (isWeekend(day)) continue;
          
          const scheduledAt = new Date(day);
          scheduledAt.setHours(parseInt(timeParts[0]));
          scheduledAt.setMinutes(parseInt(timeParts[1]));
          
          notifications.push({
            userCode: formData.userCode,
            sessionName: formData.sessionName,
            scheduledAt: scheduledAt.toISOString()
          });
        }

        if (notifications.length === 0) {
          throw new Error('No valid weekdays in selected date range');
        }

        const responses = await Promise.all(
          notifications.map(notification => 
            api.post(`/api/notifications`, notification)
          )
        );

        if (responses.every(res => res.data.success)) {
          setSuccess(true);
          setTimeout(() => navigate('/dashboard/notifications'), 1500);
        }
      } else {
        // Single notification
        const scheduledAt = new Date(formData.startDate);
        if (isWeekend(scheduledAt)) {
          throw new Error('Cannot schedule notifications on weekends');
        }

        // Set the time from singleTime input
        const timeParts = formData.singleTime.split(':');
        scheduledAt.setHours(parseInt(timeParts[0]));
        scheduledAt.setMinutes(parseInt(timeParts[1]));

        const response = await api.post(`/api/notifications`, {
          userCode: formData.userCode,
          sessionName: formData.sessionName,
          scheduledAt: scheduledAt.toISOString()
        });
        
        if (response.data.success) {
          setSuccess(true);
          setTimeout(() => navigate('/dashboard/notifications'), 1500);
        }
      }
    } catch (err) {
      setError(err.message || err.response?.data?.error || 'Failed to create notification(s)');
      console.error('Error creating notification(s):', err);
    }
  };

  const [sessions, setSessions] = useState([]);
  const [participants, setParticipants] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionRes, participantRes] = await Promise.all([
          api.get(`/api/sessions/list`),
          api.get(`/api/participants`)
        ]);
        setSessions(sessionRes.data);
        setParticipants(participantRes.data);
      } catch (err) {
        console.error("Fehler beim Laden der Daten:", err);
      }
    };
    fetchData();
  }, []);


  return (
    <div className="notification-form-container">
      <h1>Neuen Zeitplan erstellen</h1>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">
        {isRecurring ? 'Zeitplan erfolgreich erstellt!' : 'Zeitplan erfolgreich erstellt!'}
      </div>} 

      <form onSubmit={handleSubmit} className="notification-form">
        <div className="form-group">
          <label htmlFor="userCode">Teilnehmer Code</label>
          <select
            id="userCode"
            name="userCode"
            value={formData.userCode}
            onChange={handleChange}
            required
          >
            <option value="">-- Teilnehmer Auswählen --</option>
            {participants.map((p, index) => (
              <option key={index} value={p.code}>
                {p.name ? `${p.name} (${p.code})` : p.code}
              </option>
            ))}
          </select>
        </div>


        <div className="form-group">
          <label htmlFor="sessionName">Session Name</label>
          <select
            id="sessionName"
            name="sessionName"
            value={formData.sessionName}
            onChange={handleChange}
            required
          >
            <option value="">-- Session Auswählen --</option>
            {sessions.map((session, index) => (
              <option key={index} value={session.name || session}>
                {session.name || session}
              </option>
            ))}
          </select>
        </div>


        <div className="form-group checkbox-group">
          <input
            type="checkbox"
            id="recurring"
            checked={isRecurring}
            onChange={() => setIsRecurring(!isRecurring)}
          />
          <label htmlFor="recurring">Mehrere Tage</label>
        </div>

        {isRecurring ? (
          <>
            <div className="form-group">
              <label htmlFor="startDate">Startdatum (Montag-Freitag)</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label htmlFor="endDate">Enddatum (Montag-Freitag)</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                required
                min={formData.startDate}
              />
            </div>

            <div className="form-group">
              <label htmlFor="notificationTime">Uhrzeit</label>
              <input
                type="time"
                id="notificationTime"
                name="notificationTime"
                value={formData.notificationTime}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="info-message">
              Hinweis: Wochenenden (Samstag/Sonntag) werden automatisch übersprungen.
            </div>
          </>
        ) : (
          <>
          <div className="form-group">
            <label htmlFor="startDate">Datum (Montag-Freitag)</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div className="form-group">
            <label htmlFor="singleTime">Uhrzeit</label>
            <input
              type="time"
              id="singleTime"
              name="singleTime"
              value={formData.singleTime}
              onChange={handleChange}
              required
            />
          </div>
          </>
        )}

        <div className="button-group">
          <button type="submit" className="submit-button">
            {isRecurring ? 'Zeitplan erstellen' : 'Zeitplan erstellen'}
          </button>
          <button 
            type="button" 
            className="cancel-button"
            onClick={() => navigate('/dashboard/notifications')}
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateNotificationAdmin;